const CONTENT_ATTRIBUTES = new Set([
  "alt",
  "title",
  "placeholder",
  "value",
  "label",
  "href",
  "download",
]);

/**
 * `data-*` attributes that drive styling rather than carry content.
 *
 * Radix and the shadcn components style off these, and the app has 42
 * `data-[state=...]` variants across dialogs, sheets, selects, accordions and
 * checkboxes. Masked, every one of those serializes as `data-state="***"` and
 * replays in the wrong visual state: dialogs draw closed, checkboxes lose
 * their tick. That defeats the point of keeping a replay legible.
 */
const STYLING_DATA_ATTRIBUTES = new Set([
  "data-state",
  "data-side",
  "data-align",
  "data-orientation",
  "data-slot",
  "data-disabled",
  "data-highlighted",
  "data-placeholder",
  "data-motion",
  "data-swipe",
]);

const MASKED = "***";

/**
 * Masking text nodes is not enough: a recording keeps attributes as they were,
 * and several carry what someone uploaded or wrote. `alt` describes a figure,
 * `aria-label` quotes the disclosure filename, a download `href` can be a
 * presigned URL. Masking every attribute instead would hide `class`, `style`
 * and `src`, which is what makes a replay legible as a page.
 *
 * `aria-*` and `data-*` are masked as families rather than by listing the ones
 * known to carry text, so one added later is covered without anyone coming
 * back here. The styling `data-*` above are the declared exception.
 */
export function maskContentAttribute(name: string, value: string): string {
  if (STYLING_DATA_ATTRIBUTES.has(name)) return value;

  const isContent =
    CONTENT_ATTRIBUTES.has(name) ||
    name.startsWith("aria-") ||
    name.startsWith("data-");

  return isContent ? MASKED : value;
}
